import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

/**
 * GDPR cookie bar (orestbida/vanilla-cookieconsent), client-only.
 *
 * Categories:
 *  - necessary  — always on, read-only. Covers the functional cookies this app
 *                 already sets (locale, data-lang, channel, basket, currency,
 *                 fontSize, Sanctum XSRF/session).
 *  - analytics  — measurement (GTM/GA). Nothing is loaded yet; the consent
 *                 signal is pushed to dataLayer so GTM Consent Mode can pick it
 *                 up once the gtm plugin module gets enabled.
 *  - marketing  — advertising/remarketing. Same: signal only, for now.
 *
 * Texts live here rather than in the backend translation domain so the bar
 * renders on first paint even if the translations request is slow or fails.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const dataLayerPush = (payload: Record<string, unknown>) => {
    const w = window as unknown as { dataLayer?: unknown[] }
    w.dataLayer ||= []
    w.dataLayer.push(payload)
  }

  /**
   * Google Consent Mode v2 — the "update" half. `gtag('consent', 'update', …)`
   * writes into the Google tags' internal consent state; a plain dataLayer push
   * with the same key names would NOT do that. The gtag stub and the matching
   * 'default' call are injected in <head> by plugins/consent-mode.ts, which must
   * run before any container loads.
   *
   * The `cookie_consent_update` event is pushed alongside it purely as a trigger
   * hook for GTM (and for anything non-Google that wants to react).
   */
  const syncConsent = () => {
    const analytics = CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied'
    const marketing = CookieConsent.acceptedCategory('marketing') ? 'granted' : 'denied'
    const consent = {
      analytics_storage: analytics,
      ad_storage: marketing,
      ad_user_data: marketing,
      ad_personalization: marketing,
      functionality_storage: 'granted',
      security_storage: 'granted',
    }

    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag
    if (gtag) {
      gtag('consent', 'update', consent)
    } else {
      // consent-mode.ts always defines the stub; this only guards against the
      // head script having been stripped (e.g. by an aggressive proxy).
      dataLayerPush({ 0: 'consent', 1: 'update', 2: consent })
    }

    dataLayerPush({ event: 'cookie_consent_update', ...consent })
  }

  // Localized privacy-policy URL. $localePath comes from @nuxtjs/i18n; the map
  // is a fallback in case the plugin order ever puts us ahead of it.
  const privacyHref = () => {
    const localePath = (nuxtApp as unknown as { $localePath?: (r: object) => string }).$localePath
    if (localePath) {
      try {
        return localePath({ name: 'privacy-policy' })
      } catch {
        /* fall through to the static map */
      }
    }
    const code = String(nuxtApp.$i18n?.locale?.value ?? 'cs')
    return (
      { cs: '/ochrana-osobnich-udaju', en: '/en/privacy-policy', de: '/de/datenschutz' }[code] ??
      '/ochrana-osobnich-udaju'
    )
  }

  const necessaryDesc = {
    cs: 'Nezbytné pro chod webu — přihlášení, jazyk, objednávka a zabezpečení formulářů. Bez nich web nefunguje, proto je nelze vypnout.',
    en: 'Required for the site to work — sign-in, language, ordering and form security. The site cannot run without them, so they cannot be turned off.',
    de: 'Für den Betrieb der Website erforderlich — Anmeldung, Sprache, Bestellung und Formularsicherheit. Ohne sie funktioniert die Website nicht und sie können daher nicht deaktiviert werden.',
  }
  const analyticsDesc = {
    cs: 'Pomáhají nám anonymně měřit návštěvnost a chování na webu, abychom ho mohli zlepšovat.',
    en: 'Help us anonymously measure traffic and on-site behaviour so we can keep improving.',
    de: 'Helfen uns, Besucherzahlen und Verhalten anonym zu messen, damit wir die Website verbessern können.',
  }
  const marketingDesc = {
    cs: 'Umožňují zobrazovat relevantní reklamu a měřit její účinnost.',
    en: 'Allow relevant advertising to be shown and its effectiveness measured.',
    de: 'Ermöglichen relevante Werbung und die Messung ihrer Wirksamkeit.',
  }

  const SESSION = { cs: 'Relace (do zavření prohlížeče)', en: 'Session', de: 'Sitzung' }

  /**
   * Cookies this app actually sets, audited from the code (every `useCookie()`
   * call site) and cross-checked against the Set-Cookie headers of a running
   * instance. Durations are the configured `maxAge`/`expires` — keep them in
   * sync when a call site changes.
   *
   * Only `necessary` has a table: no analytics/marketing script is loaded yet,
   * so listing _ga & co. here would describe cookies that never get set.
   */
  const necessaryCookies: Array<{
    name: string
    dur: Record<'cs' | 'en' | 'de', string>
    desc: Record<'cs' | 'en' | 'de', string>
  }> = [
    {
      name: 'cc_cookie',
      dur: { cs: '182 dní', en: '182 days', de: '182 Tage' },
      desc: {
        cs: 'Uchovává tvůj souhlas s cookies, aby se lišta nezobrazovala znovu.',
        en: 'Stores your cookie consent so the bar is not shown again.',
        de: 'Speichert Ihre Cookie-Einwilligung, damit das Banner nicht erneut erscheint.',
      },
    },
    {
      name: 'locale',
      dur: SESSION,
      desc: {
        cs: 'Zvolený jazyk rozhraní.',
        en: 'Selected interface language.',
        de: 'Gewählte Sprache der Benutzeroberfläche.',
      },
    },
    {
      name: 'data-lang',
      dur: SESSION,
      desc: {
        cs: 'Jazyk, ve kterém se načítá obsah ze systému správy obsahu.',
        en: 'Language in which content is loaded from the CMS.',
        de: 'Sprache, in der Inhalte aus dem CMS geladen werden.',
      },
    },
    {
      name: 'channel',
      dur: SESSION,
      desc: {
        cs: 'Prodejní kanál, který web posílá serveru u objednávek členství.',
        en: 'Sales channel sent to the server with membership orders.',
        de: 'Verkaufskanal, der bei Mitgliedschaftsbestellungen an den Server gesendet wird.',
      },
    },
    {
      name: 'html-font-size',
      dur: { cs: '1 rok', en: '1 year', de: '1 Jahr' },
      desc: {
        cs: 'Nastavená velikost písma webu.',
        en: 'Chosen text size for the site.',
        de: 'Eingestellte Schriftgröße der Website.',
      },
    },
    {
      name: 'XSRF-TOKEN',
      // Laravel odvozuje platnost od SESSION_LIFETIME, na backendu 120 minut.
      dur: { cs: '2 hodiny', en: '2 hours', de: '2 Stunden' },
      desc: {
        cs: 'Bezpečnostní token chránící odesílané formuláře proti zneužití (CSRF).',
        en: 'Security token protecting submitted forms against CSRF attacks.',
        de: 'Sicherheitstoken, das gesendete Formulare vor CSRF-Angriffen schützt.',
      },
    },
    {
      // Laravel/Sanctum session cookie, httpOnly. The name comes from the
      // backend's SESSION_COOKIE; verified against the Set-Cookie header of
      // /sanctum/csrf-cookie (Max-Age=7200, i.e. SESSION_LIFETIME=120).
      name: 'mdclub_api_session',
      dur: { cs: '2 hodiny', en: '2 hours', de: '2 Stunden' },
      desc: {
        cs: 'Udržuje tvoje přihlášení mezi jednotlivými stránkami. Přístupná jen serveru, ne skriptům v prohlížeči.',
        en: 'Keeps you signed in across pages. Accessible to the server only, not to browser scripts.',
        de: 'Hält Ihre Anmeldung seitenübergreifend aufrecht. Nur für den Server zugänglich, nicht für Browser-Skripte.',
      },
    },
    {
      name: 'foreign-user',
      // Zapisuje se při každém přihlášení (shared-components/plugins/auth.ts,
      // afterLoginLogic), běžnému uživateli s hodnotou 'false' — proto v tabulce
      // zůstává. Popisek je záměrně neutrální, servisní režim administrace se
      // návštěvníka webu netýká.
      dur: SESSION,
      desc: {
        cs: 'Technický příznak typu přihlášení.',
        en: 'Technical flag of the sign-in type.',
        de: 'Technisches Kennzeichen der Anmeldeart.',
      },
    },
    {
      name: 'basket-guid, basket-count, basket-price, basket-is-funeral',
      // Cílový stav: maxAge 2592000 s = 30 dní. Skutečné nastavení je
      // v shared-components/composables/basketService.ts.
      dur: { cs: '30 dní', en: '30 days', de: '30 Tage' },
      desc: {
        cs: 'Rozpracovaná objednávka členství — její identifikátor, počet položek a cena.',
        en: 'Your in-progress membership order — its identifier, item count and price.',
        de: 'Ihre laufende Mitgliedschaftsbestellung — Kennung, Anzahl der Posten und Preis.',
      },
    },
  ]

  /**
   * Third-party cookies from the measurement configured in the administration
   * (`/public/settings/codes` → `codes_body_start`): Google Analytics 4,
   * property G-Z7WK66BSS1, loaded via gtag.js. That script carries
   * `data-category="analytics" type="text/plain"`, so CookieConsent keeps it
   * inert until this category is accepted — no GA cookie exists before then.
   *
   * The GTM container (GTM-TZHW29BG) loads unconditionally but sets no cookies
   * of its own; its tags obey the Consent Mode state instead.
   *
   * No marketing table: the container currently has no advertising tags
   * configured, so nothing would ever set a marketing cookie. Add rows here
   * when Ads/remarketing goes live.
   */
  const analyticsCookies: typeof necessaryCookies = [
    {
      name: '_ga',
      dur: { cs: '2 roky', en: '2 years', de: '2 Jahre' },
      desc: {
        cs: 'Google Analytics — náhodný identifikátor, díky kterému se opakovaná návštěva nepočítá jako nový uživatel.',
        en: 'Google Analytics — a random identifier so a repeat visit is not counted as a new user.',
        de: 'Google Analytics — zufällige Kennung, damit ein wiederholter Besuch nicht als neuer Nutzer zählt.',
      },
    },
    {
      // Wildcard on purpose: the suffix is the GA4 property ID (today
      // _ga_Z7WK66BSS1, from codes_body_start). Naming the pattern keeps the
      // table correct when the property is swapped in the administration.
      name: '_ga_*',
      dur: { cs: '2 roky', en: '2 years', de: '2 Jahre' },
      desc: {
        cs: 'Google Analytics — stav relace a počet návštěv pro konkrétní měřicí službu (koncovka názvu odpovídá jejímu identifikátoru).',
        en: 'Google Analytics — session state and visit count for a given property (the name suffix is the property identifier).',
        de: 'Google Analytics — Sitzungsstatus und Besuchszahl für die jeweilige Property (die Namensendung entspricht deren Kennung).',
      },
    },
  ]

  const cookieTable = (
    lang: 'cs' | 'en' | 'de',
    rows: typeof necessaryCookies = necessaryCookies
  ) => ({
    headers: {
      name: { cs: 'Název', en: 'Name', de: 'Name' }[lang],
      desc: { cs: 'Účel', en: 'Purpose', de: 'Zweck' }[lang],
      dur: { cs: 'Doba uložení', en: 'Retention', de: 'Speicherdauer' }[lang],
    },
    body: rows.map((c) => ({
      name: c.name,
      desc: c.desc[lang],
      dur: c.dur[lang],
    })),
  })

  const section = (lang: 'cs' | 'en' | 'de') => [
    {
      title: {
        cs: 'Jak cookies používáme',
        en: 'How we use cookies',
        de: 'Wie wir Cookies nutzen',
      }[lang],
      description: {
        cs: `Cookies používáme, aby web fungoval a abychom mu rozuměli. Nezbytné cookies jsou vždy aktivní, ostatní zapneš jen se svým souhlasem. Podrobnosti najdeš v <a href="${privacyHref()}" class="cc__link">Ochraně osobních údajů</a>.`,
        en: `We use cookies to run the site and to understand how it is used. Necessary cookies are always active; the rest run only with your consent. Details are in our <a href="${privacyHref()}" class="cc__link">Privacy policy</a>.`,
        de: `Wir verwenden Cookies für den Betrieb der Website und um sie zu verstehen. Notwendige Cookies sind immer aktiv, alle anderen nur mit Ihrer Einwilligung. Details finden Sie in der <a href="${privacyHref()}" class="cc__link">Datenschutzerklärung</a>.`,
      }[lang],
    },
    {
      title: { cs: 'Nezbytné cookies', en: 'Necessary cookies', de: 'Notwendige Cookies' }[lang],
      description: necessaryDesc[lang],
      linkedCategory: 'necessary',
      cookieTable: cookieTable(lang),
    },
    {
      title: { cs: 'Analytické cookies', en: 'Analytics cookies', de: 'Analyse-Cookies' }[lang],
      description: analyticsDesc[lang],
      linkedCategory: 'analytics',
      cookieTable: cookieTable(lang, analyticsCookies),
    },
    {
      title: { cs: 'Marketingové cookies', en: 'Marketing cookies', de: 'Marketing-Cookies' }[lang],
      description: marketingDesc[lang],
      linkedCategory: 'marketing',
    },
  ]

  const translation = (lang: 'cs' | 'en' | 'de') => ({
    consentModal: {
      title: {
        cs: 'Nastavení soukromí',
        en: 'Privacy settings',
        de: 'Privatsphäre-Einstellungen',
      }[lang],
      // The link to the privacy policy is kept on purpose: a first-layer notice
      // has to point to where the full detail lives.
      description: {
        cs: `Tyto webové stránky používají k poskytování služeb, personalizaci reklam a analýze návštěvnosti soubory cookie. Více v <a href="${privacyHref()}" class="cc__link">Ochraně osobních údajů</a>.`,
        en: `This website uses cookies to provide its services, personalise advertising and analyse traffic. More in our <a href="${privacyHref()}" class="cc__link">Privacy policy</a>.`,
        de: `Diese Website verwendet Cookies, um Dienste bereitzustellen, Werbung zu personalisieren und den Datenverkehr zu analysieren. Mehr in der <a href="${privacyHref()}" class="cc__link">Datenschutzerklärung</a>.`,
      }[lang],
      acceptAllBtn: { cs: 'Přijmout vše', en: 'Accept all', de: 'Alle akzeptieren' }[lang],
      acceptNecessaryBtn: {
        cs: 'Odmítnout',
        en: 'Reject all',
        de: 'Ablehnen',
      }[lang],
      showPreferencesBtn: { cs: 'Nastavení', en: 'Settings', de: 'Einstellungen' }[lang],
    },
    preferencesModal: {
      title: {
        cs: 'Nastavení cookies',
        en: 'Cookie settings',
        de: 'Cookie-Einstellungen',
      }[lang],
      acceptAllBtn: { cs: 'Přijmout vše', en: 'Accept all', de: 'Alle akzeptieren' }[lang],
      acceptNecessaryBtn: { cs: 'Odmítnout', en: 'Reject all', de: 'Ablehnen' }[lang],
      savePreferencesBtn: {
        cs: 'Uložit nastavení',
        en: 'Save settings',
        de: 'Einstellungen speichern',
      }[lang],
      closeIconLabel: { cs: 'Zavřít', en: 'Close', de: 'Schließen' }[lang],
      sections: section(lang),
    },
  })

  CookieConsent.run({
    autoShow: true,
    // Consent is a first-party functional cookie; 6 months is the usual re-ask window.
    cookie: { name: 'cc_cookie', expiresAfterDays: 182, sameSite: 'Lax' },
    guiOptions: {
      // Popup horizontally centered at the bottom of the viewport, rather than a
      // full-width bar; `box wide` puts the buttons on their own row.
      consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        // Matches _ga and _ga_<property>, the only analytics cookies in play
        // (GA4 via gtag.js from codes_body_start).
        autoClear: {
          cookies: [{ name: /^_ga/ }],
        },
      },
      marketing: {
        // Nothing sets these yet; kept so a future Ads/conversion-linker tag is
        // cleaned up on withdrawal without needing a code change.
        autoClear: {
          cookies: [{ name: /^_gcl/ }],
        },
      },
    },
    language: {
      // `en` is redirected away at the URL level but stays a valid i18n locale.
      default: 'cs',
      autoDetect: 'document',
      translations: { cs: translation('cs'), en: translation('en'), de: translation('de') },
    },
    onFirstConsent: syncConsent,
    onConsent: syncConsent,
    onChange: syncConsent,
  })

  // autoDetect reads <html lang> once at run(); keep the bar in sync when the
  // visitor switches language mid-session.
  const i18nLocale = nuxtApp.$i18n?.locale
  if (i18nLocale) {
    watch(i18nLocale, (code) => {
      CookieConsent.setLanguage(String(code))
    })
  }
})
